export const PROTOCOL_ORDER = ['vless-reality', 'trojan', 'hysteria2', 'sk6-anytls', 'sk6-direct', 'sk6-ws-cdn', 'unknown'] as const;
export type UsageProtocol = typeof PROTOCOL_ORDER[number];
export type ProtocolSource = 'native' | 'ticket';
export type ExactRatio = { numerator: string; denominator: string } | null;
export type ProtocolUsageRow = {
  protocol: UsageProtocol; label: string; totalBytes: string | null; members: number | null;
  uploadBytes: string | null; downloadBytes: string | null;
  trafficShare: ExactRatio; memberReach: ExactRatio;
  lastReceivedAt: string | null; observation: string;
};
export type ProtocolUsageDaily = { date: string; totalBytes: string | null; members: number | null; rows: ProtocolUsageRow[]; availability?: string; status?: string };
export type ProtocolUsage = {
  brand: string; source: ProtocolSource; from: string; through: string;
  timezone: 'Asia/Taipei'; timeBasis: 'ledger_received_at'; revision: string;
  cutoffAt: string | null; processedThroughAt: string | null;
  availability: 'available' | 'empty' | 'not_collected' | 'stale' | 'unsupported';
  coverage: {
    status: string; fleetCoverage: null; testTrafficExcluded: boolean;
    collectionStartedAt: string | null; expectedDays: number; projectedDays: number; missingDays: string[]; partialDays?: string[]; availableFrom?: string | null; projectionError?: string | null; tailPending?: boolean;
    excludedHeartbeatReports: string | number; missingIdentityReports: string | number;
    protocolIdentification: ExactRatio; reasons: string[];
  };
  totals: { totalBytes: string | null; members: number | null } | null;
  rows: ProtocolUsageRow[]; daily: ProtocolUsageDaily[];
};

export function protocolScopeKey(brand: string, source: ProtocolSource, from: string, through: string): string {
  return JSON.stringify([brand, source, from, through]);
}

export function compareExact(a: string | null, b: string | null): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? -1 : 1;
  return BigInt(a) < BigInt(b) ? -1 : BigInt(a) > BigInt(b) ? 1 : 0;
}

export function sortProtocolRows(rows: ProtocolUsageRow[]): ProtocolUsageRow[] {
  return [...rows].sort((a, b) => compareExact(b.totalBytes, a.totalBytes) || PROTOCOL_ORDER.indexOf(a.protocol) - PROTOCOL_ORDER.indexOf(b.protocol));
}

/** Integer arithmetic all the way through formatting, including values above Number.MAX_SAFE_INTEGER. */
export function formatProtocolBytes(value: string | null | undefined): string {
  if (value == null) return '—';
  const bytes = BigInt(value);
  const [unit, divisor] = bytes >= 1_000_000_000n ? ['GB', 1_000_000_000n] as const
    : bytes >= 1_000_000n ? ['MB', 1_000_000n] as const
      : bytes >= 1_000n ? ['KB', 1_000n] as const : ['bytes', 1n] as const;
  if (divisor === 1n) return `${bytes.toLocaleString('zh-TW')} bytes`;
  const hundredths = (bytes * 100n + divisor / 2n) / divisor;
  return `${(hundredths / 100n).toLocaleString('zh-TW')}.${(hundredths % 100n).toString().padStart(2, '0')} ${unit}`;
}

export function formatProtocolRatio(ratio: ExactRatio | undefined): string {
  if (!ratio || BigInt(ratio.denominator) === 0n) return '—';
  const numerator = BigInt(ratio.numerator);
  const denominator = BigInt(ratio.denominator);
  if (numerator > 0n && numerator * 1000n < denominator) return '<0.1%';
  const tenths = (numerator * 1000n + denominator / 2n) / denominator;
  return `${tenths / 10n}.${tenths % 10n}%`;
}

/** Only the final bounded CSS width becomes a floating point number. */
export function protocolBarWidth(ratio: ExactRatio): number {
  if (!ratio || BigInt(ratio.denominator) === 0n) return 0;
  return Number((BigInt(ratio.numerator) * 10000n) / BigInt(ratio.denominator)) / 100;
}

export function validProtocolRange(from: string, through: string, earliest: string, today: string): boolean {
  if (![from, through].every(day => /^\d{4}-\d{2}-\d{2}$/.test(day) && Number.isFinite(Date.parse(`${day}T00:00:00Z`)) && new Date(`${day}T00:00:00Z`).toISOString().slice(0, 10) === day)) return false;
  const days = (Date.parse(`${through}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000 + 1;
  return days >= 1 && days <= 30 && from >= earliest && through <= today;
}
